import React, { useState } from "react";
import Select from "react-select";
import { FaTimes, FaExchangeAlt, FaTrashAlt } from "react-icons/fa";
import { IoIosArrowForward } from "react-icons/io";

import {
  checkInAppointment,
  changeDoctorForAppointment,
  cancelAppointment,
} from "../../../services/api";

const AppointmentDetails = ({
  appointment,
  doctorIdToName,
  doctors,
  onClose,
  onCheckIn,
  onChangeDoctor,
  onCancelAppointment,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [showDoctorDropdown, setShowDoctorDropdown] = useState(false);

  const getStatusLabel = (status) => {
    switch (status) {
      case 0:
        return "Waiting";
      case 1:
        return "In-progress";
      case 2:
        return "Completed";
      case 3:
        return "Cancelled";
      default:
        return "Unknown";
    }
  };

  const doctorOptions = doctors.map((doctor) => ({
    value: doctor.doctor_id,
    label: `${doctor.first_name} ${doctor.last_name}`,
    speciality: doctor.speciality,
  }));

  const canCancelAppointment = appointment.status === 0; // Only allow cancellation for waiting appointments

  const doctorName = doctorIdToName[appointment.doctor_id] || "Unknown Doctor";

  const handleCheckIn = async () => {
    setIsLoading(true);
    try {
      await checkInAppointment(appointment.appointment_id);
      onCheckIn();
    } catch (error) {
      setError("Failed to check-in the appointment");
    }
    setIsLoading(false);
  };

  const handleChangeDoctorClick = () => {
    setShowDoctorDropdown(!showDoctorDropdown);
  };

  const handleDoctorChange = async (selectedOption) => {
    if (!selectedOption || selectedOption.value === appointment.doctor_id) {
      return; // Early return to avoid proceeding without a valid selection or if the same doctor is selected
    }

    setSelectedDoctor(selectedOption);
    setIsLoading(true);
    try {
      await onChangeDoctor(appointment.appointment_id, selectedOption.value);
    } catch (error) {
      setError("Failed to change the doctor");
      console.error("Error changing doctor:", error);
    }
    setIsLoading(false);
  };

  const handleCancelAppointment = async () => {
    setIsLoading(true);
    try {
      await cancelAppointment(appointment.appointment_id);
      onCancelAppointment();
    } catch (error) {
      setError("Failed to cancel the appointment");
    }
    setIsLoading(false);
  };
  return (
    <div className="relative p-8">
      <div
        className=" cursor-pointer border text-gray-600 bg-transparent hover:bg-gray-100 hover:text-gray-800 px-0 w-10 h-10 inline-flex justify-center items-center rounded-full text-2xl "
        onClick={onClose}
      >
        {/* <FaTimes size={24} className="text-gray-600 hover:text-gray-800" /> */}
        <IoIosArrowForward />
      </div>

      <div className="mb-8 mt-12">
        <h2 className="text-3xl font-bold text-gray-800 mb-2">
          {appointment.patient_details}
        </h2>
        <p className="text-gray-600 text-lg">Appointment Details</p>
      </div>

      <div className="mb-10">
        <div className="flex items-center mb-4">
          <p className="text-gray-600 text-lg font-semibold mr-2">Doctor:</p>
          <p className="text-2xl font-bold text-gray-800">{doctorName}</p>
        </div>
        <div className="flex items-center">
          <p className="text-gray-600 text-lg font-semibold mr-2">Status:</p>
          <p
            className={`text-2xl font-bold ${
              appointment.status === 0 ? "text-yellow-500" : "text-green-500"
            }`}
          >
            {getStatusLabel(appointment.status)}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {appointment.status === 0 && (
          <>
            <button
              className="bg-blue-500 text-white rounded-lg py-4 px-8 flex items-center justify-center hover:bg-blue-600 focus:outline-none transition duration-200 ease-in-out"
              onClick={handleCheckIn}
              disabled={isLoading}
            >
              <span className="text-lg font-semibold">Check-in</span>
            </button>
            <div className="relative">
              <Select
                value={selectedDoctor}
                onChange={handleDoctorChange}
                options={doctorOptions}
                placeholder="Select Doctor"
                isSearchable
                isLoading={isLoading}
                classNamePrefix="react-select "
                className={" flex h-full rounded-lg  border border-gray-200"}
                formatOptionLabel={(doctor) => (
                  <div className="flex items-center ">
                    <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold mr-3">
                      {`${doctor.label.split(" ")[0].charAt(0)}${doctor.label
                        .split(" ")[1]
                        .charAt(0)}`}
                    </div>
                    <div>
                      <p className="text-base font-semibold">{doctor.label}</p>
                      <p className="text-sm text-gray-500">
                        {doctor.speciality}
                      </p>
                    </div>
                  </div>
                )}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    borderRadius: "0.5rem",
                    borderColor: "transparent",
                    boxShadow: "none",
                    "&:hover": {
                      borderColor: "transparent",
                    },
                    width: "100%",
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isSelected
                      ? "#2e37a4"
                      : state.isFocused
                      ? "#f3f4f6"
                      : "white",
                    color: state.isSelected ? "white" : "#374151",
                    "&:hover": {
                      backgroundColor: "#f3f4f6",
                    },
                  }),
                }}
              />
            </div>
          </>
        )}
        {canCancelAppointment && (
          <button
            className="bg-red-500 text-white rounded-lg py-4 px-8 flex items-center justify-center hover:bg-red-600 focus:outline-none transition duration-200 ease-in-out col-span-2"
            onClick={handleCancelAppointment}
            disabled={isLoading}
          >
            <FaTrashAlt className="mr-2 text-2xl" />
            <span className="text-lg font-semibold">Cancel Appointment</span>
          </button>
        )}
      </div>
      {error && <p className="text-red-500 mt-4">{error}</p>}
    </div>
  );
};

export default AppointmentDetails;
